import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Messages } from 'primereact/messages';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import adminApi from '../api/adminApi';
import { useAuth } from '../contexts/AuthContext';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const FEATURE_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const REQUIRED_FEATURES = ['managemembers'];

const AdminFeatures = () => {
  const navigate = useNavigate();
  const msgs = useRef(null);
  const toast = useRef(null);
  const { refreshAccessContext } = useAuth();
  const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [featuresMap, setFeaturesMap] = useState({});
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureEnabled, setNewFeatureEnabled] = useState(false);

  const entries = useMemo(
    () => Object.entries(featuresMap).sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
    [featuresMap]
  );

  const handleError = (error, fallbackMessage) => {
    if (handleAccessError(error)) {
      return;
    }

    const detail =
      (error && error.response && error.response.data) ||
      (error && error.message) ||
      fallbackMessage;

    if (msgs.current) {
      msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
    }
  };

  const loadFeatures = async () => {
    try {
      setLoading(true);
      clearAccessIssue();
      if (msgs.current) msgs.current.clear();
      const next = await adminApi.getFeatures();
      const normalized = { ...(next || {}) };
      REQUIRED_FEATURES.forEach((featureName) => {
        if (!Object.prototype.hasOwnProperty.call(normalized, featureName)) {
          normalized[featureName] = false;
        }
      });
      setFeaturesMap(normalized);
    } catch (error) {
      handleError(error, 'Failed to load features.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeatures();
  }, []);

  const updateFeature = async (featureName, enabled) => {
    try {
      setUpdating(true);
      await adminApi.setGlobalFeature(featureName, enabled);
      await refreshAccessContext();
      setFeaturesMap((prev) => ({ ...prev, [featureName]: Boolean(enabled) }));
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: '',
          detail: `Feature '${featureName}' updated.`,
        });
      }
    } catch (error) {
      handleError(error, 'Failed to update feature.');
    } finally {
      setUpdating(false);
    }
  };

  const addNewFeature = async () => {
    const featureName = String(newFeatureName || '').trim();

    if (!FEATURE_NAME_PATTERN.test(featureName)) {
      if (msgs.current) {
        msgs.current.show({
          severity: 'warn',
          summary: '',
          detail: 'Use lowercase letters/numbers with dashes, e.g. notification-settings.',
        });
      }
      return;
    }

    await updateFeature(featureName, newFeatureEnabled);
    setNewFeatureName('');
  };

  const progressbar = (
    <div className="card mt-3">
      <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
    </div>
  );

  if (accessIssue) {
    return <AccessState {...accessIssue} />;
  }

  return (
    <Card className="justify-content-md-center mt-5">
      <Container fluid="md">
        <div className="panel">
          <div className="flex align-content-center flex-wrap">
            <Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex mb-3" />
            <h4 className="flex mt-2 mb-3">Admin - Global Features</h4>
          </div>
        </div>

        <Messages ref={msgs} />
        <Toast ref={toast} />
        {loading && progressbar}

        {!loading && (
          <div className="card p-3 mb-3">
            {entries.length === 0 && <p>No features found.</p>}

            {entries.map(([name, enabled]) => (
              <div key={name} className="flex align-items-center justify-content-between mb-3">
                <div>
                  <strong>{name}</strong>
                </div>
                <InputSwitch
                  checked={Boolean(enabled)}
                  onChange={(e) => updateFeature(name, e.value)}
                  disabled={updating}
                />
              </div>
            ))}
          </div>
        )}

        <div className="card p-3">
          <h5>Add New Feature Flag</h5>
          <div className="flex align-items-center flex-wrap gap-3">
            <InputText
              placeholder="feature-name"
              value={newFeatureName}
              onChange={(e) => setNewFeatureName(e.target.value)}
              style={{ minWidth: '260px' }}
            />
            <div className="flex align-items-center gap-2">
              <span>Enabled</span>
              <InputSwitch
                checked={newFeatureEnabled}
                onChange={(e) => setNewFeatureEnabled(e.value)}
              />
            </div>
            <Button
              label="Add"
              icon="pi pi-plus"
              onClick={addNewFeature}
              disabled={updating}
            />
          </div>
        </div>
      </Container>
    </Card>
  );
};

export default AdminFeatures;
